/**
 * The single DOM node every top-centre status banner renders into.
 *
 * Why a shared host at all: the banners used to position themselves independently, and two of
 * them were `position: fixed` in the **layout** viewport while the SC-255 zoom notice was pinned
 * to the **visible** viewport and counter-scaled. Under WebKit magnification those two spaces
 * come apart — the fixed pair render at the magnified size and drift out of position while the
 * zoom notice holds a constant apparent size, so the group stops reading as one family however
 * carefully their top offsets are computed. Arithmetic can reconcile the positions; it cannot
 * reconcile the sizes.
 *
 * One host in one space removes the whole class of problem: the banners become siblings in a
 * flex column, so they match in size, stack by DOM order, and cannot overlap.
 *
 * A module-level store rather than React context because the banners render in unrelated
 * subtrees — one inside `LiveKitRoomConnector`'s room context, one at the App root — and reach
 * the host through a portal, which keeps each component where its context lives while its DOM
 * lands in the shared column.
 */

let host: HTMLElement | null = null
const listeners = new Set<() => void>()

/** Called by `StatusBannerViewport` as a ref callback; `null` on unmount. */
export function setStatusBannerHost(el: HTMLElement | null): void {
  if (host === el) return
  host = el
  listeners.forEach((fn) => fn())
}

export function getStatusBannerHost(): HTMLElement | null {
  return host
}

export function subscribeStatusBannerHost(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Test-only: module state would otherwise leak between cases. */
export function resetStatusBannerHostForTest(): void {
  host = null
  listeners.clear()
}
